import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { driver } from 'driver.js';
import { filter, map } from 'rxjs';
import {
  Tour,
  TUTORIAL_SEED,
  TutorialConfig,
  TutorialId,
  WELCOME_TOUR,
} from '../config/tutorials.config';

/**
 * Orchestrates in-app interactive tutorials.
 *
 * Owns the route-to-tutorial resolution, the runtime registry, and
 * the single driver.js invocation, so feature code never imports
 * driver.js directly.
 */
@Injectable({ providedIn: 'root' })
export class TutorialService {
  private readonly _router = inject(Router);

  /** Tutorials available at runtime, keyed by `routePrefix`. */
  private readonly _registry = signal<ReadonlyMap<string, TutorialConfig>>(
    new Map(TUTORIAL_SEED.map((t) => [t.routePrefix, t])),
  );

  /**
   * Whether the welcome dialog should be shown. Set by MainLayout on
   * first access; cleared when the dialog component opens itself.
   */
  private readonly _welcomeRequested = signal(false);
  readonly welcomeRequested = this._welcomeRequested.asReadonly();

  /** Request that the welcome dialog be opened. */
  requestWelcome(): void {
    this._welcomeRequested.set(true);
  }

  /** Acknowledge the welcome request (called by the dialog after opening). */
  acknowledgeWelcome(): void {
    this._welcomeRequested.set(false);
  }

  /**
   * Whether the tutorial picker dialog should be opened.
   * Lets in-page entry points (e.g. an icon next to a page title)
   * trigger the same picker that the sidebar Tutorial button opens,
   * without needing a direct viewChild reference.
   */
  private readonly _pickerRequested = signal(false);
  readonly pickerRequested = this._pickerRequested.asReadonly();

  /** Request that the tutorial picker dialog be opened. */
  requestPicker(): void {
    this._pickerRequested.set(true);
  }

  /** Acknowledge the picker request (called by the picker after opening). */
  acknowledgePicker(): void {
    this._pickerRequested.set(false);
  }

  private readonly _currentPath = toSignal(
    this._router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this._router.url),
    ),
    { initialValue: this._router.url },
  );

  /**
   * Tutorial config registered for the current route, or `null` if
   * none. Longest matching `routePrefix` wins.
   */
  readonly currentTutorial = computed<TutorialConfig | null>(() => {
    const path = this._currentPath();
    let best: TutorialConfig | null = null;
    for (const t of this._registry().values()) {
      if (!path.startsWith(t.routePrefix)) continue;
      if (!best || t.routePrefix.length > best.routePrefix.length) {
        best = t;
      }
    }
    return best;
  });

  /**
   * Resolves the tutorial for the current route, dropping any variant
   * whose `requiresElement` selector is not in the DOM.
   * Call at picker-open time (DOM stable), not from a reactive computed.
   */
  resolveAvailableTutorial(): TutorialConfig | null {
    const config = this.currentTutorial();
    if (!config) return null;
    const tutorials = config.tutorials.filter(
      (t) => !t.requiresElement || !!document.querySelector(t.requiresElement),
    );
    if (tutorials.length === 0) return null;
    return tutorials.length === config.tutorials.length
      ? config
      : { ...config, tutorials };
  }

  /** Adds/replaces a tutorial config in the runtime registry. */
  register(config: TutorialConfig): void {
    const next = new Map(this._registry());
    next.set(config.routePrefix, config);
    this._registry.set(next);
  }

  /** Removes a tutorial from the runtime registry. */
  unregister(routePrefix: string): void {
    if (!this._registry().has(routePrefix)) return;
    const next = new Map(this._registry());
    next.delete(routePrefix);
    this._registry.set(next);
  }

  /**
   * Starts a tutorial variant on the current route. No-op if no
   * config matches or the variant is unknown.
   */
  start(id: TutorialId): void {
    const config = this.currentTutorial();
    if (!config) return;
    const variant = config.tutorials.find((t) => t.id === id);
    if (!variant) return;
    this.startTour(variant, {
      disableInteraction: id === TutorialId.Overview,
    });
  }

  /**
   * Starts the global welcome tour. Non-interactive (look, don't touch).
   */
  startWelcomeTour(): void {
    this.startTour(WELCOME_TOUR, { disableInteraction: true });
  }

  /**
   * Starts a tour applying the app-wide driver.js look and feel.
   * Filters out steps whose `element` selector is not present in the DOM.
   */
  startTour(tour: Tour, options?: { disableInteraction?: boolean }): void {
    const interactive = !(options?.disableInteraction ?? false);
    const run = (): void => {
      // For non-interactive tours (Overview, Welcome) we filter steps whose
      // target is not currently in the DOM, so role- or tab-gated UI does
      // not break the tour. Interactive tours (CreationGuide) skip this:
      // their later steps target elements that materialize as the user
      // progresses (e.g. fields inside a sheet that opens at step 1).
      const steps = interactive
        ? [...tour.steps]
        : tour.steps.filter((s) => {
            if (typeof s.element !== 'string') return true;
            return !!document.querySelector(s.element);
          });
      if (steps.length === 0) return;

      driver({
        showProgress: true,
        progressText: '{{current}} di {{total}}',
        nextBtnText: 'Avanti',
        prevBtnText: 'Indietro',
        doneBtnText: 'Fine',
        overlayOpacity: 0.6,
        disableActiveInteraction: options?.disableInteraction ?? false,
        overlayClickBehavior: () => undefined,
        stagePadding: tour.stagePadding ?? 4,
        steps,
      }).drive();
    };

    if (!tour.onBeforeStart) {
      run();
      return;
    }
    tour.onBeforeStart();
    setTimeout(run, 50);
  }
}
